import Medusa from "@medusajs/js-sdk"
import { lastFailedCall } from "./observability"

/**
 * Cookie-session auth: sdk.auth.login() creates an httpOnly session cookie, so
 * no token ever touches localStorage. Requires the API on the same origin
 * (Vite proxy locally, path routing on terify.<domain> in production).
 */
export const sdk = new Medusa({
  baseUrl: import.meta.env.VITE_MEDUSA_BACKEND_URL || window.location.origin,
  auth: { type: "session" },
  debug: import.meta.env.DEV && !import.meta.env.VITE_SDK_QUIET,
})

export type ApiError = { status?: number; message: string; code?: string }

/**
 * For a server error: " Reference: <request id>" (the id the server logged it under), so a
 * screenshot or a pasted message can be matched to the logs (lib/observability.ts).
 */
export function errorReference(e: unknown): string {
  try {
    const status = e && typeof e === "object" ? Number((e as any).status) : NaN
    const own = e && typeof e === "object" && typeof (e as any).requestId === "string" ? ((e as any).requestId as string) : null
    const last = lastFailedCall()
    // Server errors: this call's id, or (SDK errors don't carry headers) the last 5xx call's.
    if (Number.isFinite(status) && status >= 500) {
      const id = own ?? (last && last.status === status ? last.id : null)
      return id ? ` Reference: ${id}` : ""
    }
    // A call that never got an answer (network, CDN): the last such call's id.
    if (!Number.isFinite(status) && last && last.status === 0 && /fetch|network|load failed/i.test(String((e as any)?.message ?? ""))) return ` Reference: ${last.id}`
  } catch {}
  return ""
}

export function errorMessage(e: unknown): string {
  return baseErrorMessage(e) + errorReference(e)
}

function baseErrorMessage(e: unknown): string {
  const msg = e && typeof e === "object" && "message" in e ? String((e as any).message ?? "").trim() : ""
  if (msg) return msg
  // No message: the answer wasn't the API's JSON (e.g. the CDN's own error page when the server took too long).
  const status = e && typeof e === "object" ? Number((e as any).status) : NaN
  if (status === 504 || status === 502) return "The server took too long to answer. Try again in a minute."
  if (status === 429) return "Too many requests. Wait a minute and try again."
  if (status === 401) return "Your session has ended. Sign in again."
  if (status === 403) return "This request was blocked. Reload the page and try again."
  if (Number.isFinite(status) && status >= 500) return `The server had a problem (HTTP ${status}). Try again.`
  if (Number.isFinite(status) && status > 0) return `The request failed (HTTP ${status}).`
  return "Something went wrong. Check your connection and try again."
}

/**
 * Plain JSON fetch against the admin API that keeps the error `code`
 * (account_deactivated, session_revoked, …). The JS SDK's FetchError only
 * carries message + status, and the admin needs the code to explain sign-outs.
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const base = import.meta.env.VITE_MEDUSA_BACKEND_URL || window.location.origin
  const res = await fetch(base + path, {
    credentials: "include",
    ...init,
    headers: { accept: "application/json", ...(init.body ? { "content-type": "application/json" } : {}), ...init.headers },
  })
  const body = await res.json().catch(() => null)
  if (!res.ok) {
    const err = new Error(body?.message ?? res.statusText) as Error & { status: number; code?: string; requestId?: string }
    err.status = res.status
    err.code = body?.code
    err.requestId = res.headers.get("x-request-id") ?? undefined
    throw err
  }
  return body as T
}
